import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ReportBlockControls from '../components/moderation/ReportBlockControls';
import FavoriteButton from '../components/FavoriteButton';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderProfile = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorited, setFavorited] = useState(false);
  
  // Message modal state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchProviderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProviderData = async () => {
    try {
      // Fetch user data first
      const providerRes = await axios.get(`${API}/users/${id}`);
      setProvider(providerRes.data);
      
      // Then fetch services (won't fail if empty)
      try {
        const servicesRes = await axios.get(`${API}/services?provider_id=${id}`);
        setServices(servicesRes.data.filter(s => s.provider_id === id));
      } catch {
        setServices([]);
      }
      
      // Then fetch reviews (consolidated from all services and jobs)
      try {
        const reviewsRes = await axios.get(`${API}/providers/${id}/reviews`);
        setReviews(reviewsRes.data);
      } catch {
        setReviews([]);
      }

      // Then fetch credentials
      try {
        const credsRes = await axios.get(`${API}/credentials/user/${id}`);
        setCredentials(credsRes.data);
      } catch {
        setCredentials([]);
      }

      // Whether the current user has favorited this provider
      if (user && user.id !== id) {
        try {
          const favRes = await axios.get(`${API}/favorites/ids`);
          setFavorited((favRes.data?.provider_ids || []).includes(id));
        } catch {
          setFavorited(false);
        }
      }
    } catch (error) {
      console.error('Error fetching provider data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageProvider = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowMessageModal(true);
  };

  const sendMessageToProvider = async () => {
    if (!messageText.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/providers/${id}/message`, {
        message: messageText.trim()
      });
      
      setShowMessageModal(false);
      setMessageText('');
      
      // Navigate to the inquiry chat
      if (response.data.inquiry_id) {
        navigate(`/inquiries/${response.data.inquiry_id}`);
      } else {
        navigate('/inquiries');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">{t('providerProfile.loading')}</div>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <p>{t('providerProfile.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="provider-profile-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-8 mb-8" data-testid="provider-header">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-4 sm:gap-6">
              {provider.avatar ? (
                <img 
                  src={provider.avatar.startsWith('http') ? provider.avatar : `${process.env.REACT_APP_BACKEND_URL}${provider.avatar}`} 
                  alt={provider.name}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover flex-shrink-0"
                  data-testid="provider-avatar"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-blue-500 rounded-full flex items-center justify-center text-white text-4xl font-bold">
                  {provider.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold mb-2 flex flex-wrap items-center gap-2 break-words" data-testid="provider-name">
                  {provider.name}
                  {provider.id_verified && (
                    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-sm font-semibold px-2.5 py-0.5 rounded-full" data-testid="verified-badge">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      Verified
                    </span>
                  )}
                </h1>
                  {provider.rating > 0 && (
                  <div className="flex items-center gap-2 mb-2" data-testid="provider-rating">
                    <span className="text-yellow-500 text-2xl">★</span>
                    <span className="text-2xl font-bold">{provider.rating}</span>
                    <span className="text-gray-500">({provider.total_reviews} {t('providerProfile.reviews')})</span>
                  </div>
                )}
                {provider.location && (
                  <p className="text-gray-600" data-testid="provider-location">📍 {provider.location}</p>
                )}
              </div>
            </div>
            
            {user && user.id !== provider.id && (
              <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
                <button
                  onClick={handleMessageProvider}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold flex items-center justify-center gap-2 transition whitespace-nowrap"
                  data-testid="message-provider-button"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {t('providerProfile.sendMessage')}
                </button>
                <FavoriteButton
                  providerId={provider.id}
                  user={user}
                  initialFavorited={favorited}
                  variant="labeled"
                  onChange={(pid, isFav) => setFavorited(isFav)}
                />
                <ReportBlockControls targetUserId={provider.id} targetName={provider.name} />
              </div>
            )}
          </div>

          {provider.bio && (
            <div className="mt-6 pt-6 border-t" data-testid="provider-bio">
              <h3 className="font-bold text-lg mb-2">{t('providerProfile.about')}</h3>
              <p className="text-gray-700 leading-relaxed">{provider.bio}</p>
            </div>
          )}

          {provider.skills && provider.skills.length > 0 && (
            <div className="mt-6 pt-6 border-t" data-testid="provider-skills">
              <h3 className="font-bold text-lg mb-3">{t('providerProfile.skills')}</h3>
              <div className="flex flex-wrap gap-2">
                {provider.skills.map((skill, index) => (
                  <span key={index} className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Credentials & Clearances */}
        {credentials.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-testid="credentials-section">
            <h2 className="text-2xl font-bold mb-1">{t('providerProfile.credentials')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('providerProfile.credentialsNote')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {credentials.map((cred) => {
                const labels = {
                  barangay_clearance: 'Barangay Clearance',
                  police_clearance: 'Police Clearance',
                  nbi_clearance: 'NBI Clearance',
                  certification: 'Certification',
                  other: 'Document',
                };
                // Use watermarked URL if user is logged in and viewing someone else's profile
                const isOwner = user?.id === provider.id;
                const token = localStorage.getItem('token');
                const imgSrc = isOwner
                  ? (cred.image_url.startsWith('http') ? cred.image_url : `${process.env.REACT_APP_BACKEND_URL}${cred.image_url}`)
                  : `${API}/credentials/${cred.id}/view?token=${token}`;
                return (
                  <div key={cred.id} className="text-center" data-testid={`credential-${cred.id}`}>
                    <img
                      src={imgSrc}
                      alt={labels[cred.type] || cred.type}
                      className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition select-none"
                      style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
                      draggable={false}
                      onContextMenu={(e) => !isOwner && e.preventDefault()}
                      onClick={() => {
                        if (isOwner) {
                          window.open(cred.image_url.startsWith('http') ? cred.image_url : `${process.env.REACT_APP_BACKEND_URL}${cred.image_url}`, '_blank');
                        } else {
                          window.open(`${API}/credentials/${cred.id}/view?token=${token}`, '_blank');
                        }
                      }}
                    />
                    <p className="text-xs text-gray-600 mt-1.5 font-medium">{labels[cred.type] || cred.type}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Services */}
        <div className="mb-8" data-testid="services-section">
          <h2 className="text-2xl font-bold mb-6">{t('providerProfile.servicesOffered')}</h2>
          {services.length === 0 ? (
            <p className="text-gray-500" data-testid="no-services-message">{t('providerProfile.noServices')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => navigate(`/services/${service.id}`)}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition cursor-pointer card-hover"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-100 text-blue-600 text-sm px-3 py-1 rounded-full">
                      {service.category}
                    </span>
                    {service.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-500">★</span>
                        <span className="text-sm font-semibold">{service.rating}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {service.currency === 'PHP' ? '₱' : service.currency === 'EUR' ? '€' : '$'}{service.price}
                      </p>
                      <p className="text-xs text-gray-500">{service.price_unit}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-lg shadow-md p-8" data-testid="reviews-section">
          <h2 className="text-2xl font-bold mb-6">{t('providerProfile.reviewsTitle')}</h2>
          {reviews.length === 0 ? (
            <p className="text-gray-500" data-testid="no-reviews-message">{t('providerProfile.noReviews')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reviews.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50" data-testid={`review-${review.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                        {review.service_title || 'Unknown Service'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-yellow-500">★</span>
                          <span className="font-semibold">{review.rating || review.average_rating || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {review.client_name || review.reviewer_name || 'Anonymous'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs">
                        <span className="line-clamp-2">{review.comment || '—'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                        {review.created_at ? new Date(review.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-2xl">
              <div className="text-white">
                <h3 className="font-bold text-lg">Message {provider.name}</h3>
                <p className="text-sm text-orange-100">Start a conversation</p>
              </div>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageText('');
                }}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition"
                data-testid="close-message-modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">
                Send a message to {provider.name} to discuss their services or ask questions.
              </p>
              
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Hi! I'm interested in your services..."
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none"
                rows={4}
                disabled={sendingMessage}
                data-testid="message-textarea"
              />
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setMessageText('');
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-semibold transition"
                  disabled={sendingMessage}
                >
                  Cancel
                </button>
                <button
                  onClick={sendMessageToProvider}
                  disabled={sendingMessage || !messageText.trim()}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 transition flex items-center justify-center gap-2"
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProviderProfile;